import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import { GEBKeys, type GEBLine, type Pair } from 'shur-api'
import type { DataBlock, IconDataSpan, SingleBlock } from 'de-zurich-ui-library'
import { t } from '@/i18n'
import {
  createCoverageConceptField,
  createDataBlock,
  createDataField,
  findValueByKey,
  validateDetails
} from './SHURDataCreatorHelpers'
import {
  ALL_COVERED_PERILS,
  BUILDING_COVERAGE_KEYS,
  BUILDING_KEY_LABEL_MAP,
  BUILDING_PLACE_KEYS,
  BUILDING_SCOPE_KEYS
} from './SHURBuildingKeyConfig'

const instantCoverageIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_instantCoverage',
  isRouterLink: false
}

function createCoveredPerilsField(details: Pair[]): SingleBlock {
  const value = getLabelByPerilKey(details)
  return {
    label: t('shurDetails.sections.BUILDING.COVERAGE.title'),
    value,
    hide: !value
  } as SingleBlock
}

function getLabelByPerilKey(details: Pair[]): string {
  const labels: string[] = []
  ALL_COVERED_PERILS.forEach((perilKey) => {
    if (findValueByKey(details, perilKey) === 'Ja') {
      labels.push(t(BUILDING_KEY_LABEL_MAP[perilKey]!))
    }
  })
  return labels.join(', ')
}

export class SHURDataBuildingCreator implements DataBlockArrayCreator {
  createDatablockArray(gebLine: GEBLine): DataBlock[] {
    return [
      createBuildingGlobalData(gebLine),
      createBuildingPlace(gebLine),
      createBuildingCoverage(gebLine)
    ]
  }
}

// Versicherungsumfang
function createBuildingGlobalData(gebLine: GEBLine): DataBlock {
  const details = validateDetails(gebLine.details)
  if (!details)
    return createDataBlock('shurDetails.sections.BUILDING.SCOPE_OF_INSURANCE', null, [])

  const coverageConceptField = createCoverageConceptField(details, gebLine.productSubType)

  const buildingTypeValue = findValueByKey(details, GEBKeys.BuildingType)
  const buildingTypeTranslated = buildingTypeValue
    ? (() => {
        const translationKey = `shurDetails.sections.BUILDING.BUILDING_TYPE_VALUES.${buildingTypeValue}`
        const translated = t(translationKey)
        return translated === translationKey ? buildingTypeValue : translated
      })()
    : null

  return createDataBlock('shurDetails.sections.BUILDING.SCOPE_OF_INSURANCE', details, [
    {
      label: t('shurDetails.sections.BUILDING.BUILDING_TYPE'),
      value: buildingTypeTranslated
    },
    coverageConceptField,
    createCoveredPerilsField(details),
    {
      label: t(BUILDING_KEY_LABEL_MAP[GEBKeys.BuildcovNaturalharzardPeril]!),
      value: t('shurDetails.sections.BUILDING.COVERAGE.NATURALHARZARD_DESCRIPTION'),
      hide: findValueByKey(details, GEBKeys.BuildcovNaturalharzardPeril) != 'Ja'
    } as SingleBlock,
    ...BUILDING_SCOPE_KEYS.map((key) =>
      createDataField(BUILDING_KEY_LABEL_MAP[key]!, details, key)
    ),
    {
      label: t(`shurDetails.sections.${GEBKeys.InstantCoverage}`),
      value: details.find((entry) => entry.key === GEBKeys.InstantCoverage)?.value ?? '',
      hide: findValueByKey(details, GEBKeys.InstantCoverage) != 'Ja',
      iconSpan: instantCoverageIconData
    }
  ])
}

// Versicherungsort
function createBuildingPlace(gebLine: GEBLine): DataBlock {
  const details = validateDetails(gebLine.details)
  if (!details)
    return createDataBlock('shurDetails.sections.insuredPlace.title', null, [])

  return createDataBlock(
    'shurDetails.sections.insuredPlace.title',
    details,
    BUILDING_PLACE_KEYS.map((key) =>
      createDataField(BUILDING_KEY_LABEL_MAP[key]!, details, key)
    )
  )
}

// Versicherte Sachen
function createBuildingCoverage(gebLine: GEBLine): DataBlock {
  const details = validateDetails(gebLine.details)
  if (!details)
    return createDataBlock('shurDetails.sections.BUILDING.INSURED_THINGS.title', null, [])

  return createDataBlock(
    'shurDetails.sections.BUILDING.INSURED_THINGS.title',
    details,
    BUILDING_COVERAGE_KEYS.map((key) =>
      createDataField(BUILDING_KEY_LABEL_MAP[key]!, details, key)
    )
  )
}
